import React, { useEffect, useState } from 'react';
import { getLscVodSyncReadiness } from '../api';
import type { LscVodSyncReadiness } from '../types';
import ErrorBanner from './ErrorBanner';
import LoadingState from './LoadingState';
import { useLeague } from '../hooks/useLeague';
import CfbInlineTeamChip from './CfbInlineTeamChip';
import {
  formatVodSyncReadinessHeadline,
  formatVodSyncSkippedHeadline,
  VOD_SYNC_READINESS_COPY,
  vodSyncReasonCounts,
} from '../syncReadiness';

interface VodSyncReadinessPanelProps {
  leagueId: string;
  refreshKey?: number;
}

const VodSyncReadinessPanel: React.FC<VodSyncReadinessPanelProps> = ({
  leagueId,
  refreshKey = 0,
}) => {
  const [readiness, setReadiness] = useState<LscVodSyncReadiness | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const { league } = useLeague();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getLscVodSyncReadiness(leagueId);
        if (!cancelled) setReadiness(data);
      } catch {
        if (!cancelled) {
          setError(
            "Couldn't load who is sync-ready. Refresh, or check Members for Twitch links."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leagueId, refreshKey]);

  if (loading) {
    return (
      <div className="card" data-testid="vod-sync-readiness-panel">
        <LoadingState message="Loading sync readiness..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" data-testid="vod-sync-readiness-panel">
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
      </div>
    );
  }

  if (!readiness) {
    return null;
  }

  const reasonCounts = vodSyncReasonCounts(readiness);

  return (
    <div className="card space-y-4" data-testid="vod-sync-readiness-panel">
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">VOD Sync Ready</h2>
        <p className="text-2xl font-semibold text-white" data-testid="vod-sync-readiness-headline">
          {formatVodSyncReadinessHeadline(readiness)}
        </p>
        <p className="text-sm text-secondary-400 mt-1" data-testid="vod-sync-skipped-headline">
          Skipped: {formatVodSyncSkippedHeadline(readiness)}
        </p>
        {readiness.total_current_members === 0 && (
          <p className="text-sm text-secondary-400 mt-2" data-testid="vod-sync-zero-members-copy">
            Add members and link Twitch before a sync will find VODs.
          </p>
        )}
      </div>

      <div className="text-sm text-secondary-400 space-y-2">
        <p>{VOD_SYNC_READINESS_COPY.headline}</p>
        <p>{VOD_SYNC_READINESS_COPY.pendingLink}</p>
        <p data-testid="vod-sync-no-guess-copy">{VOD_SYNC_READINESS_COPY.noGuess}</p>
      </div>

      {reasonCounts.length > 0 && (
        <dl className="grid gap-2 text-sm sm:grid-cols-2" data-testid="vod-sync-reason-counts">
          {reasonCounts.map(({ reason, count, label }) => (
            <div key={reason}>
              <dt className="text-secondary-500">{label}</dt>
              <dd className="text-secondary-200">{count}</dd>
            </div>
          ))}
        </dl>
      )}

      {readiness.skipped_members && readiness.skipped_members.length > 0 && (
        <div>
          <button
            type="button"
            className="text-sm text-primary-300 hover:text-primary-200"
            onClick={() => setExpanded((open) => !open)}
            data-testid="vod-sync-skipped-toggle"
          >
            {expanded ? 'Hide skipped members' : 'Show skipped members'}
          </button>
          {expanded && (
            <div
              className="mt-3 overflow-x-auto rounded-lg border border-secondary-700"
              data-testid="vod-sync-skipped-list"
            >
              <table className="min-w-full text-sm">
                <thead className="text-secondary-400">
                  <tr>
                    <th className="text-left py-2 px-3">Member</th>
                    <th className="text-left py-2 px-3">Side</th>
                    <th className="text-left py-2 px-3">Status</th>
                    <th className="text-left py-2 px-3">Reason</th>
                    <th className="text-left py-2 px-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-700">
                  {readiness.skipped_members.map((member) => (
                    <tr key={member.member_id ?? member.display_name}>
                      <td className="py-2 px-3 text-secondary-200">
                        {member.display_name ?? '—'}
                      </td>
                      <td className="py-2 px-3 text-secondary-300">
                        {member.current_side_label ? (
                          league?.game_key === 'cfb' ? (
                            <CfbInlineTeamChip label={member.current_side_label} showLabel />
                          ) : (
                            member.current_side_label
                          )
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="py-2 px-3 capitalize text-secondary-300">
                        {member.status ?? '—'}
                      </td>
                      <td className="py-2 px-3 text-secondary-300 capitalize">
                        {(member.reason ?? 'unknown').replace(/_/g, ' ')}
                      </td>
                      <td className="py-2 px-3 text-secondary-400">{member.action ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VodSyncReadinessPanel;
